import { Params } from '@angular/router';
import { matchesText, vehicleName } from './panel.calc';
import { DocumentType, Financing, PanelDocument, Policy, Vehicle } from './panel.models';

/**
 * Dokumenty (Issue 13, 22): filtr po pojeździe i typie, szukanie po nazwie, numerze i źródle, kafle i link do
 * rekordu źródłowego. Lista przychodzi z API od najnowszych — tu tylko filtrowanie, bez przeliczania.
 */

/** Kolejność typów jak w prototypie (chipy filtra); „Inne” na końcu, gdy takie pliki są. */
export const DOCUMENT_TYPES: DocumentType[] = ['Polisa', 'Umowa', 'Rejestracja', 'Wydanie', 'Faktura', 'Oferta', 'Inne'];

/** Filtr pojazdu: „all”, id pojazdu („16”, „c268”) albo „none” — dokumenty bez pojazdu („Inne”). */
export type DocumentVehicleFilter = 'all' | 'none' | string;

export interface DocumentFilter {
    vehicle: DocumentVehicleFilter;
    type: DocumentType | 'all';
    query: string;
}

export interface DocumentSources {
    policies: Policy[];
    financing: Financing[];
    vehicles: Vehicle[];
}

export interface DocumentLabels {
    policy: (number: string | null) => string;
    financing: (number: string | null) => string;
    case: (id: string) => string;
}

/** Etykieta źródła dokumentu: „Polisa 1059827741”, „Umowa TED4330/LO”, „Mercedes GLC 300”, „Sprawa #268”. */
export function documentSource(doc: PanelDocument, sources: DocumentSources, labels: DocumentLabels): string {
    const id = doc.source.id;

    switch (doc.source.kind) {
        case 'policy':
            return labels.policy(sources.policies.find((p) => String(p.id) === id)?.number ?? doc.number);
        case 'financing':
            return labels.financing(sources.financing.find((f) => f.id === id)?.contractNumber ?? doc.number);
        case 'vehicle':
            return vehicleName(sources.vehicles.find((v) => v.id === id)) || id;
        default:
            return labels.case(id);
    }
}

/** Link do rekordu źródłowego; plik sprawy otwiera etap, z którego pochodzi (Issue 14). */
export function documentRoute(doc: PanelDocument): { link: (string | number)[]; queryParams: Params | null } {
    const id = doc.source.id;

    switch (doc.source.kind) {
        case 'policy':
            return { link: ['/polisy', id], queryParams: null };
        case 'financing':
            return { link: ['/finansowanie', id], queryParams: null };
        case 'vehicle':
            return { link: ['/pojazdy', id], queryParams: { zakladka: 'dokumenty' } };
        default:
            return { link: ['/sprawy', id], queryParams: doc.stageCode ? { etap: doc.stageCode } : null };
    }
}

export function filterDocuments(docs: PanelDocument[], filter: DocumentFilter, source: (doc: PanelDocument) => string): PanelDocument[] {
    return docs.filter(
        (d) =>
            (filter.vehicle === 'all' || (filter.vehicle === 'none' ? !d.vehicleId : d.vehicleId === filter.vehicle)) &&
            (filter.type === 'all' || d.type === filter.type) &&
            matchesText(filter.query, d.name, d.number, source(d))
    );
}

/** Kafle: wszystkie pasujące, polisy, umowy, pozostałe (rejestracja, wydanie, faktury, oferty, inne). */
export function documentTiles(docs: PanelDocument[]): { all: number; policies: number; contracts: number; other: number } {
    const policies = docs.filter((d) => d.type === 'Polisa').length;
    const contracts = docs.filter((d) => d.type === 'Umowa').length;

    return { all: docs.length, policies, contracts, other: docs.length - policies - contracts };
}
